// Customer Calibration Overview — ONE ROW PER (customer, hub) lane.
//
// Day-of-week factors are learned and stored per hub lane (a customer can ship a
// different DOW shape to each hub). The daily base is a per-customer level.
//
//  • Interval  — complete Saturday→Friday weeks of actuals to learn from (2/3/4).
//  • Calibrate — learns this lane's DOW from that hub's actuals, and the customer
//                base from the same actuals divided by the lane's allocation %.
//  • Apply     — saves the lane DOW (customer_lanes.dow_factors) + the customer base.
//  • Reset     — clears this lane's DOW, reverting the hub to the customer profile.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Card, Input, Select } from "../../components/ui";
import { getErrorMessage } from "../../lib/errors";
import { addDays, fmtDate, today } from "../../lib/format";
import type { Customer, Hub, HubEvent } from "../../lib/types";
import {
  activeAllocationPct,
  calibrateDailyBase,
  calibrateDowFactors,
  dowForCustomerHub,
  isHoliday,
  seasonalMultiplier,
} from "../../lib/forecast/engine";
import { listCustomers, setCustomerBase } from "../customers/customersApi";
import { clearLaneDowFactors, saveLaneDowFactors } from "../customers/lanesApi";
import { listHubs } from "../hubs/hubsApi";
import { listAccountCustomerActuals, type CustomerActual } from "../actuals/customerActualsApi";
import { listHubEvents } from "../hubevents/hubEventsApi";
import { calibrationStatus, calibrationWindow } from "./calibration";

const INTERVALS = [2, 3, 4];
// Sat→Fri columns mapped to the engine's getDay() index (0=Sun..6=Sat).
const SAT_FRI: Array<[string, number]> = [
  ["Sat", 6],
  ["Sun", 0],
  ["Mon", 1],
  ["Tue", 2],
  ["Wed", 3],
  ["Thu", 4],
  ["Fri", 5],
];

/** A single customer→hub lane to calibrate. */
interface LaneRow {
  customer: Customer;
  hubId: string;
  key: string; // `${customerId}|${hubId}`
}

export default function CalibrationOverview() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [actuals, setActuals] = useState<CustomerActual[]>([]);
  const [events, setEvents] = useState<HubEvent[]>([]);
  const [intervals, setIntervals] = useState<Record<string, number>>({}); // by customerId
  const [bases, setBases] = useState<Record<string, string>>({}); // by customerId
  const [dows, setDows] = useState<Record<string, string[]>>({}); // by lane key
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [start] = useState(today());
  const [query, setQuery] = useState("");
  const [hubFilter, setHubFilter] = useState("__all__");

  const refresh = useCallback(async () => {
    if (!accountId) return;
    try {
      const wide = calibrationWindow(start, 4); // widest window we might need
      const [c, h, a, ev] = await Promise.all([
        listCustomers(accountId),
        listHubs(accountId),
        listAccountCustomerActuals(accountId, wide.start, start),
        listHubEvents(accountId),
      ]);
      const active = c.filter((x) => x.active);
      setCustomers(active);
      setHubs(h);
      setActuals(a);
      setEvents(ev);
      const iv: Record<string, number> = {};
      const bs: Record<string, string> = {};
      const dw: Record<string, string[]> = {};
      for (const cust of active) {
        iv[cust.id] = cust.calibrationWeeks ?? 3;
        bs[cust.id] = String(cust.dailyBase);
        for (const hubId of new Set(cust.allocations.map((al) => al.hubId))) {
          dw[`${cust.id}|${hubId}`] = dowForCustomerHub(cust, hubId).map((x) => String(x));
        }
      }
      setIntervals(iv);
      setBases(bs);
      setDows(dw);
    } catch (e) {
      setError(getErrorMessage(e, "Failed to load calibration data."));
    }
  }, [accountId, start]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const hubName = useMemo(() => {
    const m = new Map(hubs.map((h) => [h.id, h.name]));
    return (id: string) => m.get(id) ?? "?";
  }, [hubs]);

  // One row per (customer, hub) allocation, grouped by customer.
  const allRows = useMemo<LaneRow[]>(() => {
    const out: LaneRow[] = [];
    for (const customer of customers) {
      for (const hubId of new Set(customer.allocations.map((a) => a.hubId))) {
        out.push({ customer, hubId, key: `${customer.id}|${hubId}` });
      }
    }
    return out;
  }, [customers]);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allRows.filter(
      (r) =>
        (hubFilter === "__all__" || r.hubId === hubFilter) &&
        (q === "" || r.customer.name.toLowerCase().includes(q) || (r.customer.code ?? "").toLowerCase().includes(q))
    );
  }, [allRows, query, hubFilter]);

  const seasonalToday = seasonalMultiplier(start);

  /** This customer's own actuals at this hub, by date, within the interval window. */
  function windowFor(cust: Customer, hubId: string) {
    const weeks = intervals[cust.id] ?? 3;
    const win = calibrationWindow(start, weeks);
    const byDate = new Map<string, number>();
    for (const a of actuals) {
      if (a.date < win.start || a.date > win.end) continue;
      if (a.hubId !== hubId || a.customerId !== cust.id) continue;
      byDate.set(a.date, (byDate.get(a.date) ?? 0) + a.actualVolume);
    }
    let holidayCount = 0;
    for (let d = win.start; d <= win.end; d = addDays(d, 1)) if (isHoliday(d)) holidayCount++;
    const eventsInWindow = events.filter(
      (e) =>
        e.active !== false &&
        e.hubId === hubId &&
        e.startDate <= win.end &&
        e.endDate >= win.start &&
        (e.mode !== "by_customer" ||
          (e.customerOverrides ?? []).some((o) => o.customerId === cust.id))
    );
    const status = calibrationStatus({
      weeks,
      daysWithData: byDate.size,
      holidayCount,
      eventCount: eventsInWindow.length,
    });
    return { weeks, win, byDate, status, eventsInWindow };
  }

  function setInterval(custId: string, weeks: number) {
    setIntervals((p) => ({ ...p, [custId]: weeks }));
    setMsg(null);
  }
  function setDow(key: string, idx: number, val: string) {
    setDows((p) => {
      const arr = [...(p[key] ?? ["", "", "", "", "", "", ""])];
      arr[idx] = val;
      return { ...p, [key]: arr };
    });
    setMsg(null);
  }

  function calibrate(row: LaneRow) {
    const { customer: cust, hubId, key } = row;
    const { byDate } = windowFor(cust, hubId);
    const obs = [...byDate].map(([date, value]) => ({ date, value }));
    const f = calibrateDowFactors(obs);
    if (!f) {
      setError(`No actuals for ${cust.name} @ ${hubName(hubId)} in the selected window.`);
      return;
    }
    // The customer base is the whole-customer level, so divide this hub's actuals
    // by its allocation share before learning the base.
    const alloc = activeAllocationPct(cust.allocations, hubId, start) || 100;
    const adjusted = obs.map((o) => ({ date: o.date, value: o.value / (alloc / 100) }));
    const b = calibrateDailyBase(adjusted, f);
    setDows((p) => ({ ...p, [key]: f.map((x) => String(x)) }));
    if (b != null) setBases((p) => ({ ...p, [cust.id]: String(b) }));
    setError(null);
    setMsg(
      `${cust.name} @ ${hubName(hubId)}: calibrated (base ${b ?? "—"}, DOW updated). Review and Apply.`
    );
  }

  async function apply(row: LaneRow) {
    if (!accountId) return;
    const { customer: cust, hubId, key } = row;
    const base = Number(bases[cust.id]);
    const dow = (dows[key] ?? []).map(Number);
    if (!(base >= 0) || dow.length !== 7 || dow.some((n) => Number.isNaN(n))) {
      setError(`${cust.name} @ ${hubName(hubId)}: daily base and all 7 DOW factors must be valid numbers.`);
      return;
    }
    setSavingKey(key);
    setError(null);
    try {
      await saveLaneDowFactors(accountId, cust.id, hubId, dow);
      await setCustomerBase(accountId, cust.id, {
        dailyBase: base,
        calibrationWeeks: intervals[cust.id] ?? 3,
      });
      setMsg(`${cust.name} @ ${hubName(hubId)}: applied.`);
      await refresh();
    } catch (e) {
      setError(getErrorMessage(e, "Apply failed."));
    } finally {
      setSavingKey(null);
    }
  }

  async function reset(row: LaneRow) {
    if (!accountId) return;
    const { customer: cust, hubId, key } = row;
    if (!confirm(`Clear calibrated DOW factors for ${cust.name} @ ${hubName(hubId)}?`)) return;
    setSavingKey(key);
    setError(null);
    try {
      await clearLaneDowFactors(accountId, cust.id, hubId);
      setMsg(`${cust.name} @ ${hubName(hubId)}: DOW reset to the customer profile.`);
      await refresh();
    } catch (e) {
      setError(getErrorMessage(e, "Reset failed."));
    } finally {
      setSavingKey(null);
    }
  }

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  const statusClass: Record<string, string> = {
    ok: "bg-emerald-50 text-emerald-700",
    warn: "bg-amber-50 text-amber-700",
    bad: "bg-red-50 text-red-700",
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold tracking-tight text-slate-900">Calibration Overview</h1>
        {msg && <span className="text-sm text-emerald-600">{msg}</span>}
      </div>
      <p className="text-xs text-slate-500">
        One row per customer→hub lane. DOW factors are learned and stored <em>per hub</em>; the daily
        base is per customer. Interval = complete Saturday→Friday weeks of that hub's actuals.
        Seasonal factor today: ×{seasonalToday.toFixed(2)}. Edit any value, then Apply.
      </p>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-64">
            <Input placeholder="Search customer name or code…" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <div className="w-48">
            <Select value={hubFilter} onChange={(e) => setHubFilter(e.target.value)}>
              <option value="__all__">All hubs</option>
              {hubs.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </Select>
          </div>
          <span className="text-xs text-slate-400">{rows.length} lanes</span>
        </div>
      </Card>

      <Card className="max-h-[70vh] overflow-auto p-0">
        {rows.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">No active customers with hub allocations.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 [&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:bg-white">
                <th className="px-3 py-2 font-medium">Customer</th>
                <th className="px-3 py-2 font-medium">Hub</th>
                <th className="px-2 py-2 text-right font-medium">Alloc</th>
                <th className="px-2 py-2 font-medium">Interval</th>
                <th className="px-2 py-2 text-right font-medium">Daily base</th>
                {SAT_FRI.map(([lbl]) => (
                  <th key={lbl} className="px-1 py-2 text-right font-medium">
                    {lbl}
                  </th>
                ))}
                <th className="px-2 py-2 font-medium">Status</th>
                <th className="px-2 py-2 font-medium">Last calib</th>
                <th className="px-2 py-2 font-medium">Hub events</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const { customer: c, hubId, key } = row;
                const { status, eventsInWindow, byDate } = windowFor(c, hubId);
                const noActuals = byDate.size === 0; // nothing to learn from at this hub
                const firstOfCustomer = i === 0 || rows[i - 1].customer.id !== c.id;
                return (
                  <tr
                    key={key}
                    className={`border-b border-slate-100 last:border-0 ${
                      firstOfCustomer ? "" : "border-slate-50"
                    }`}
                  >
                    <td className="px-3 py-1.5 font-medium text-slate-900">
                      {firstOfCustomer ? c.name : <span className="text-slate-300">↳</span>}
                    </td>
                    <td className="px-3 py-1.5 text-slate-600">{hubName(hubId)}</td>
                    <td className="px-2 py-1.5 text-right text-slate-600">
                      {activeAllocationPct(c.allocations, hubId, start)}%
                    </td>
                    <td className="px-2 py-1">
                      <Select
                        value={String(intervals[c.id] ?? 3)}
                        onChange={(e) => setInterval(c.id, Number(e.target.value))}
                        className="w-16"
                      >
                        {INTERVALS.map((n) => (
                          <option key={n} value={n}>
                            {n}w
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="w-24 px-2 py-1">
                      <Input
                        type="number"
                        min={0}
                        value={bases[c.id] ?? ""}
                        onChange={(e) => {
                          setBases((p) => ({ ...p, [c.id]: e.target.value }));
                          setMsg(null);
                        }}
                      />
                    </td>
                    {SAT_FRI.map(([lbl, idx]) => (
                      <td key={lbl} className="w-14 px-0.5 py-1">
                        {/* Plain input (not the shared <Input>): tight padding + text-xs
                            + hidden number spinners so the decimal is fully visible. */}
                        <input
                          type="number"
                          step="any"
                          value={dows[key]?.[idx] ?? ""}
                          onChange={(e) => setDow(key, idx, e.target.value)}
                          className="w-full rounded border border-slate-300 px-1 py-1 text-right text-xs outline-none focus:border-brand-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        />
                      </td>
                    ))}
                    <td className="px-2 py-1.5">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass[status.level]}`}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-slate-500">{fmtDate(c.lastCalibratedAt)}</td>
                    <td
                      className={`px-2 py-1.5 ${eventsInWindow.length ? "text-amber-700" : "text-slate-400"}`}
                      title={eventsInWindow
                        .map((e) => `${e.type} ${fmtDate(e.startDate)}→${fmtDate(e.endDate)}`)
                        .join("; ")}
                    >
                      {eventsInWindow.length === 0
                        ? "—"
                        : `${eventsInWindow.length} (${eventsInWindow.map((e) => e.type).join(", ")})`}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          onClick={() => calibrate(row)}
                          disabled={noActuals}
                          title={noActuals ? "No actuals at this hub in the selected window" : ""}
                        >
                          Calibrate
                        </Button>
                        <Button
                          onClick={() => apply(row)}
                          disabled={savingKey === key || noActuals}
                          title={
                            noActuals
                              ? "No actuals — nothing to calibrate or save from this lane"
                              : ""
                          }
                        >
                          {savingKey === key ? "…" : "Apply"}
                        </Button>
                        <Button variant="ghost" onClick={() => reset(row)} disabled={savingKey === key}>
                          Reset
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
